import { useFetcher } from '@remix-run/react';
import { Pencil, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { MentionStaffMember } from '~/components/ui/MentionDropdown';
import { MentionTextarea } from '~/components/ui/MentionTextarea';
import { MessageContent } from '~/components/ui/MessageContent';
import type { DayOperationsReferral } from '~/types/day-operations';
import { deserializeMentionText } from '~/utils/mention-helpers';

type InternalNote = DayOperationsReferral['internalNotes'][number];

type InternalNotesPanelProps = {
  referralId: string;
  initialNotes: InternalNote[];
  onClose: () => void;
  unreadMentionNoteCount?: number;
  onMarkedRead?: () => void;
  /** Demo Builder: skip note/staff APIs and keep notes in parent state. Default off. */
  localOnly?: boolean;
  staff?: MentionStaffMember[];
  onCreate?: (content: string) => void;
  initialComposerText?: string;
  /** Demo Builder: open the mention picker when the composer mounts. Default off. */
  openMentionsOnMount?: boolean;
  /** Demo Builder: keep the mention list above the Day-Prep overlay. Default 50. */
  mentionDropdownZIndex?: number;
};

function NoteCard({
  note,
  staff,
  onChanged,
  localOnly = false,
}: {
  note: InternalNote;
  staff: MentionStaffMember[];
  onChanged: () => void;
  localOnly?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [editMentions, setEditMentions] = useState<Array<{ id: string; displayText: string }>>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const editContainerRef = useRef<HTMLDivElement>(null);
  const editFetcher = useFetcher<{ success?: boolean; error?: string }>();
  const deleteFetcher = useFetcher<{ success?: boolean; error?: string }>();

  const isEdited = note.updatedAt !== note.createdAt;
  const timestamp = new Date(note.createdAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  useEffect(() => {
    if (editFetcher.state === 'idle' && editFetcher.data?.success) {
      setIsEditing(false);
      onChanged();
    }
  }, [editFetcher.state, editFetcher.data?.success, onChanged]);

  useEffect(() => {
    if (deleteFetcher.state === 'idle' && deleteFetcher.data?.success) {
      onChanged();
    }
  }, [deleteFetcher.state, deleteFetcher.data?.success, onChanged]);

  if (isEditing) {
    return (
      <div className="rounded-lg border border-purple-500/50 bg-gray-800 p-3">
        <div ref={editContainerRef}>
          <MentionTextarea
            name="editContent"
            value={editContent}
            onChange={setEditContent}
            staff={staff}
            variant="dark"
            rows={3}
            initialMentions={editMentions}
            disabled={editFetcher.state !== 'idle'}
            showToolbar
          />
        </div>
        {editFetcher.data?.error && (
          <p className="mt-1 text-xs text-red-400">{editFetcher.data.error}</p>
        )}
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() => {
              const hiddenInput = editContainerRef.current?.querySelector<HTMLInputElement>(
                'input[name="editContent"]'
              );
              const serializedContent = hiddenInput?.value || editContent;
              const fd = new FormData();
              fd.set('intent', 'edit');
              fd.set('noteId', note.id);
              fd.set('content', serializedContent);
              editFetcher.submit(fd, { method: 'POST', action: '/api/referral-notes' });
            }}
            disabled={editFetcher.state !== 'idle' || !editContent.trim()}
            className="rounded bg-purple-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {editFetcher.state !== 'idle' ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setIsEditing(false);
              setEditContent(note.content);
              setEditMentions([]);
            }}
            className="text-xs text-gray-400 hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-lg bg-gray-800 p-3">
      <div className="mb-1 flex items-center justify-between">
        <p className="text-xs text-gray-400">
          {note.authorName} · {timestamp}
          {isEdited && <span className="ml-1 italic">(edited)</span>}
        </p>
        {note.isOwn && !localOnly && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => {
                const { displayText, mentions } = deserializeMentionText(note.content);
                setEditContent(displayText);
                setEditMentions(mentions);
                setIsEditing(true);
              }}
              className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-white"
              aria-label="Edit note"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-red-400"
              aria-label="Delete note"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      <MessageContent text={note.content} className="whitespace-pre-wrap text-sm text-gray-200" />

      {showDeleteConfirm && (
        <div className="mt-2 flex items-center gap-2 rounded border border-red-500/30 bg-red-900/20 p-2">
          <span className="text-xs text-red-300">Delete this note?</span>
          <button
            type="button"
            onClick={() => {
              const fd = new FormData();
              fd.set('intent', 'delete');
              fd.set('noteId', note.id);
              deleteFetcher.submit(fd, { method: 'POST', action: '/api/referral-notes' });
            }}
            disabled={deleteFetcher.state !== 'idle'}
            className="rounded bg-red-600 px-2 py-0.5 text-xs text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleteFetcher.state !== 'idle' ? 'Deleting…' : 'Delete'}
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(false)}
            className="text-xs text-gray-400 hover:text-white"
          >
            Cancel
          </button>
        </div>
      )}

      {deleteFetcher.data?.error && (
        <p className="mt-1 text-xs text-red-400">{deleteFetcher.data.error}</p>
      )}
    </div>
  );
}

export function InternalNotesPanel({
  referralId,
  initialNotes,
  onClose,
  unreadMentionNoteCount,
  onMarkedRead,
  localOnly = false,
  staff: localStaff,
  onCreate,
  initialComposerText = '',
  openMentionsOnMount = false,
  mentionDropdownZIndex,
}: InternalNotesPanelProps) {
  const [newNote, setNewNote] = useState(initialComposerText);
  const createFetcher = useFetcher<{ success?: boolean; error?: string }>();
  const notesFetcher = useFetcher<{ notes: InternalNote[] }>();
  const staffFetcher = useFetcher<{ staff: MentionStaffMember[] }>();
  const newNoteContainerRef = useRef<HTMLDivElement>(null);
  const markReadFetcher = useFetcher<{ success?: boolean }>();
  const markAllRead = () => {
    if (localOnly) return;
    const fd = new FormData();
    fd.set('intent', 'mark-referral-read');
    fd.set('referralId', referralId);
    fd.set('sourceType', 'NOTE');
    markReadFetcher.submit(fd, { method: 'POST', action: '/api/mention-reads' });
  };

  useEffect(() => {
    if (markReadFetcher.state === 'idle' && markReadFetcher.data?.success) {
      onMarkedRead?.();
    }
  }, [markReadFetcher.state, markReadFetcher.data, onMarkedRead]);

  const notes = localOnly ? initialNotes : (notesFetcher.data?.notes ?? initialNotes);

  const reloadNotes = useCallback(() => {
    if (localOnly) return;
    notesFetcher.load(`/api/referral-notes?referralId=${referralId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralId, localOnly]);

  useEffect(() => {
    if (localOnly) return;
    staffFetcher.load(`/api/referral-staff?referralId=${referralId}&scope=own`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralId, localOnly]);

  const staff = localOnly ? (localStaff ?? []) : (staffFetcher.data?.staff ?? []);

  useEffect(() => {
    if (createFetcher.state === 'idle' && createFetcher.data?.success) {
      setNewNote('');
      reloadNotes();
    }
  }, [createFetcher.state, createFetcher.data?.success, reloadNotes]);

  return (
    <div className="flex w-96 shrink-0 flex-col border-l border-gray-600 bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-600 px-3 py-2">
        <h3 className="text-sm font-semibold text-white">Administrative notes (company only)</h3>
        <div className="flex items-center gap-3">
          {(unreadMentionNoteCount ?? 0) > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              disabled={markReadFetcher.state !== 'idle'}
              className="text-xs font-semibold text-purple-300 hover:text-purple-100 disabled:opacity-50"
            >
              Mark all as read
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            aria-label="Close notes panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="border-b border-gray-600 px-3 py-1.5 text-xs text-gray-400">
        Medical history, medications, etc. Not shared with other companies.
      </p>

      <div className="border-b border-gray-600 p-3">
        <div ref={newNoteContainerRef}>
          <MentionTextarea
            name="content"
            value={newNote}
            onChange={setNewNote}
            staff={staff}
            placeholder="Add a note..."
            variant="dark"
            rows={2}
            hint="@ to mention"
            disabled={createFetcher.state !== 'idle'}
            showToolbar
            openMentionsOnMount={openMentionsOnMount}
            mentionDropdownZIndex={mentionDropdownZIndex}
          />
        </div>
        {createFetcher.data?.error && (
          <p className="mt-1 text-xs text-red-400">{createFetcher.data.error}</p>
        )}
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const hiddenInput =
                newNoteContainerRef.current?.querySelector<HTMLInputElement>(
                  'input[name="content"]'
                );
              const serializedContent = hiddenInput?.value || newNote;
              if (localOnly) {
                onCreate?.(serializedContent);
                setNewNote('');
                return;
              }
              const fd = new FormData();
              fd.set('intent', 'create');
              fd.set('referralId', referralId);
              fd.set('content', serializedContent);
              createFetcher.submit(fd, { method: 'POST', action: '/api/referral-notes' });
            }}
            disabled={createFetcher.state !== 'idle' || !newNote.trim()}
            className="rounded bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {createFetcher.state !== 'idle' ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
        {notes.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No notes yet</p>
        ) : (
          notes.map(note => (
            <NoteCard
              key={note.id}
              note={note}
              staff={staff}
              onChanged={reloadNotes}
              localOnly={localOnly}
            />
          ))
        )}
      </div>
    </div>
  );
}
